import { ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideEnvironmentInitializer, provideZoneChangeDetection } from '@angular/core';

import { environment } from './environments/environment';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { ADPModule } from './app/core/adp.module';
import { OktaAuthModule } from '@okta/okta-angular';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { provideServiceWorker } from '@angular/service-worker';
import { AppComponent } from './app/app.component';
import OktaAuth from '@okta/okta-auth-js';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { routes } from './app/app.routes';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { registerAppIcons } from './app/shared/icons.register';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { PreInitService, PGHAppLoadService, WINDOW_PROVIDERS } from './app/core/core';
import { ServerErrorInterceptor, HttpRequestInterceptor, MockPlanDetailsInterceptor } from './app/core/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { GlobalErrorHandler } from './app/core/core';

const oktaAuth = new OktaAuth({
  clientId: window['pghconfig']?.okta.clientId,
  issuer: window['pghconfig']?.okta.issuer,
  redirectUri: window.location.origin + '/login/callback',
  scopes: window['pghconfig']?.okta.scopes,
});

const globalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

// platformBrowserDynamic().bootstrapModule(AppModule).catch(err => console.error(err));
// Function to wait for configuration
function waitForConfig(): Promise<void> {
  // console.log('config check fired');
  return new Promise((resolve) => {
    const checkConfig = () => {
      if (window['pghconfig']?.okta) {
        // console.log('Config loaded');
        resolve();
      } else {
        // console.log('Waiting to load config');
        setTimeout(checkConfig, 100); // Check every 100ms
      }
    };
    checkConfig();
  });
}

// Bootstrap the Angular application once the configuration is available
waitForConfig().then(() => {
  // console.log('load app');
  bootstrapApplication(AppComponent, {
    providers: [
      provideZoneChangeDetection(),
      provideRouter(
        routes,
        withInMemoryScrolling({
          scrollPositionRestoration: 'top',
          anchorScrolling: 'enabled',
        })
      ),
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
      provideServiceWorker('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:30000',
      }),
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: { floatLabel: 'always', hideRequiredMarker: false, subscriptSizing: 'static' },
      },
      provideEnvironmentInitializer(() => {
        const reg = inject(MatIconRegistry);
        const san = inject(DomSanitizer);
        registerAppIcons(reg, san);
      }),
      provideAppInitializer(() => {
        const init = inject(PreInitService);
        inject(PGHAppLoadService);
        return init.loadConfig();
      }),
      WINDOW_PROVIDERS,
      {
        // Local-only: short-circuits plan-details GETs to a static asset when running on
        // localhost AND Buildconfig.useMockPlanDetails is true. No-op otherwise.
        provide: HTTP_INTERCEPTORS,
        useClass: MockPlanDetailsInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ServerErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpRequestInterceptor,
        multi: true,
      },
      { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
      { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 4000 } },
      // CDK 21 renders overlays via the Popover API (browser top layer), which sits above any z-index.
      // Third-party popups mounted on <body> (TinyMCE dialogs in the CMS edit modals) would be hidden behind them.
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      {
        // processes all errors
        provide: ErrorHandler,
        useClass: GlobalErrorHandler,
      },
      // unavoidable forRoot modules
      importProvidersFrom(ADPModule.forRoot(null), OktaAuthModule.forRoot({ oktaAuth }), NgIdleKeepaliveModule.forRoot()),
    ],
  }).catch((err) => console.error(err));
});
